import { GetCategoriesIcon, GetSportIcon } from 'assets/icons';
import BetBuilderIcon from 'assets/images/eventView/betBuilder.svg?react';
import CoursesIcon from 'assets/images/eventView/courses.svg?react';
import cn from 'classnames';
import { MarketTypesState } from 'features/eventLive/interface/interface';
import NavigationTabs from 'features/eventPreMatch/component/navigationTabs';
import { EventState, GamesState } from 'features/eventPreMatch/interface/sportPrematch';
import { t } from 'i18next';
import moment from 'moment';
import { Dispatch, RefObject, SetStateAction, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { TRACKER_TABS } from 'shared/constants/common';
import { useBetBuilderOldViewFeatureFlag } from 'shared/featureFlags';
import { useNewMobileUiManager } from 'shared/hooks/useNewMobileUiManager';
import { formatDate, getParticipants } from 'shared/util/utility';
import BetBuilderCreator from '../betBuilderCreator';
import OldBetBuilderCreator from '../OldBetBuilderCreator';
import PreMatchTrackerArrow from './component/preMatchTrackerArrow';
import PreMatchTrackerTab from './component/preMatchTrackerTab';
import './styles.scss';

interface PrematchTrackerProps {
	eventDetailData: EventState;
	marketTypes: string[];
	changeCurrentTabMainState: (currentTab: string, currentMarketTypeState: MarketTypesState[]) => void;
	trackerPrematchRef: RefObject<HTMLDivElement>;
	selectedOddsArray: GamesState[];
	onClickRemoveGame: (elem: number) => void;
	clickOnCross: any;
	onAddBetToCoupon: () => void;
	totalOdds: number;
	setTotalOdds: Dispatch<SetStateAction<number>>;
	hasSameLeagueEvents: boolean;
	setFilteredList: Dispatch<SetStateAction<GamesState[]>>;
	activeTrackerTab: any;
	setActiveTrackerTab: any;
	activePillTab: string;
	setActivePillTab: any;
	findNextAndPreviousEvents: any;
	sameLeagueEvents: any;
	adjacentEvents: { previous: string; next: string };
	hasBetBuilderBets: boolean;
	isFootball?: boolean;
	filteredGames: any;
	filterByMarketType: any;
	groupedEvents?: any;
	setFilteredGames: any;
}

interface PropsParam {
	id: string;
	sportName: string;
	parentName: string;
	categoryName: string;
}

const PrematchTracker = ({
	eventDetailData,
	hasBetBuilderBets,
	setActivePillTab,
	marketTypes,
	findNextAndPreviousEvents,
	changeCurrentTabMainState,
	trackerPrematchRef,
	sameLeagueEvents,
	selectedOddsArray,
	onClickRemoveGame,
	clickOnCross,
	onAddBetToCoupon,
	totalOdds,
	activePillTab,
	setFilteredList,
	activeTrackerTab,
	setActiveTrackerTab,
	adjacentEvents,
	hasSameLeagueEvents,
	filteredGames,
	setFilteredGames,
}: PrematchTrackerProps) => {
	const { sportId, categoryId, parentId, categoryName, eventStart, backgroundURL } = eventDetailData;
	const betBuilderCreatorRef = useRef<HTMLDivElement>(null);
	const params = useParams<PropsParam>();
	const participants = getParticipants(eventDetailData);
	const isSingleParticipant = participants.length === 1;
	const firstParticipant = participants[0];
	const secondParticipant = participants[1];
	const eventStartTime = moment(eventStart).format(' HH:mm');
	const eventDate = formatDate(eventStart, 'DD.MM.YYYY', true);
	const isBetBuilderOldView = useBetBuilderOldViewFeatureFlag();
	const showNewUI = useNewMobileUiManager();

	useEffect(() => {
		if (sameLeagueEvents) {
			findNextAndPreviousEvents(sameLeagueEvents, params);
		}
	}, [sameLeagueEvents, eventDetailData]);

	const changeCurrentTab = (activePillTab: string) => {
		window.scroll(0, 0);
		document.getElementById('tabAnimationWrapper')?.classList.add('slideFromTop');

		if (activePillTab === 'All') {
			changeCurrentTabMainState(activePillTab, eventDetailData.marketTypes);
		} else if (activePillTab === 'Builder') {
			changeCurrentTabMainState(activePillTab, []);
		} else {
			let currentMarketType: MarketTypesState[] = [];
			const selectedMarketType = eventDetailData.marketTypes?.find((marketType: any) => {
				return String(marketType.marketType) === activePillTab;
			});
			if (selectedMarketType) {
				currentMarketType = [selectedMarketType];
			}

			changeCurrentTabMainState(activePillTab, currentMarketType);
		}
	};

	const handleActiveTrackerTab = (activeTrackerTab: string) => {
		setActiveTrackerTab(activeTrackerTab);
		if (activeTrackerTab === TRACKER_TABS.COURSES) {
			setActivePillTab('All');
		} else {
			setActivePillTab('Builder');
		}
	};

	const preMatchTabs = [
		{
			title: t('courses'),
			icon: <CoursesIcon />,
			isActive: activeTrackerTab === TRACKER_TABS.COURSES,
			onClick: () => handleActiveTrackerTab(TRACKER_TABS.COURSES),
		},
		{
			disabled: !hasBetBuilderBets,
			title: t('betBuilder'),
			icon: <BetBuilderIcon />,
			isActive: activeTrackerTab === TRACKER_TABS.BET_BUILDER,
			onClick: () => handleActiveTrackerTab(TRACKER_TABS.BET_BUILDER),
		},
	];

	const prematchArrows = [
		{ link: adjacentEvents.previous, direction: 'left' as const },
		{ link: adjacentEvents.next, direction: 'right' as const },
	];

	return (
		<>
			<section
				id="trackerRef"
				className={cn('preMatchTracker', {
					'!p-0 !md:pt-[11px] -mt-[8px] -md:mt-0 !top-[calc(env(safe-area-inset-top)_+_60px)] !md:top-[calc(env(safe-area-inset-top)_+_102px)]':
						showNewUI,
				})}
				ref={trackerPrematchRef}
			>
				<div
					className={cn('', {
						'pt-[8px] md:pt-[11px]': showNewUI,
					})}
				>
					<div
						style={{ backgroundImage: `url(${backgroundURL})` }}
						className={cn('preMatchTracker__wrapper', {
							'preMatchTracker__wrapper--withImage': backgroundURL,
						})}
					>
						{hasSameLeagueEvents && prematchArrows.map((item) => <PreMatchTrackerArrow link={item.link} direction={item.direction} />)}
						<div className="preMatchTracker__icons">
							<GetSportIcon id={sportId} />
							<GetCategoriesIcon
								category={{
									sportId: sportId,
									parentCategory: categoryId,
									categoryId: parentId,
								}}
							/>
							<div className="preMatchTracker__eventName">{categoryName}</div>
						</div>
						<div className="preMatchTracker__info">
							<div
								className={cn('preMatchTracker__participants', {
									'preMatchTracker__participants--single': isSingleParticipant,
									'preMatchTracker__participants--double': !isSingleParticipant,
								})}
							>
								{isSingleParticipant ? (
									<>
										<div className="preMatchTracker__participants__time">
											{eventDate}
											<br />
											{eventStartTime}
										</div>
										<div className="preMatchTracker__participants__participant">{firstParticipant}</div>
									</>
								) : (
									<>
										<div className="preMatchTracker__participants__participant">{firstParticipant}</div>
										<div className="preMatchTracker__participants__time">
											{eventDate}
											<br />
											{eventStartTime}
										</div>
										<div className="preMatchTracker__participants__participant">{secondParticipant}</div>
									</>
								)}
							</div>
						</div>
						<div className="preMatchTracker__tabs">
							{preMatchTabs.map((item, index) => (
								<PreMatchTrackerTab
									key={`prematchtrackertab--${index}`}
									disabled={item.disabled}
									icon={item.icon}
									title={item.title}
									onClick={item.onClick}
									isActive={item.isActive}
								/>
							))}
						</div>
					</div>
					{activeTrackerTab !== TRACKER_TABS.BET_BUILDER && (
						<NavigationTabs
							filteredGames={filteredGames}
							marketTypes={marketTypes}
							eventDetailData={eventDetailData}
							setFilteredList={setFilteredList}
							activePillTab={activePillTab}
							changeCurrentTab={changeCurrentTab}
							setFilteredGames={setFilteredGames}
						/>
					)}
					{isBetBuilderOldView?.value && activeTrackerTab === TRACKER_TABS.BET_BUILDER && (
						<>
							<OldBetBuilderCreator
								BetBuilderCreatorRef={betBuilderCreatorRef}
								eventDetailData={eventDetailData}
								selectedOddsArray={selectedOddsArray}
								totalOdds={totalOdds}
								onClickRemoveGame={onClickRemoveGame}
								clickOnCross={clickOnCross}
								onAddBetToCoupon={onAddBetToCoupon}
							/>
						</>
					)}
				</div>
			</section>
			{!isBetBuilderOldView?.value && activeTrackerTab === TRACKER_TABS.BET_BUILDER && (
				<>
					<BetBuilderCreator
						selectedOddsArray={selectedOddsArray}
						totalOdds={totalOdds}
						onClickRemoveGame={onClickRemoveGame}
						clickOnCross={clickOnCross}
						onAddBetToCoupon={onAddBetToCoupon}
					/>
				</>
			)}
		</>
	);
};

export default PrematchTracker;
